/**
 * Time Entry mappers for UI consumption. Keep API-shape conditionals out of components.
 */

import { formatDate, formatTime } from "../../utils/dateUtils";
import { getWorkItemTypeIconConfig } from "../Issues/issueMappers";
import { mapWorkItemsListResponse } from "../Projects/projectMappers";

export const formatMinutesToDuration = (minutes) => {
  const n = Number(minutes) || 0;
  const h = Math.floor(n / 60);
  const m = n % 60;
  return `${h}:${m.toString().padStart(2, "0")}`;
};

/**
 * GET /work-items/my/all → rows like `{ id, name, projectName }` or legacy `{ title, issueKey, projectId }`.
 */
export function mapMyWorkItemsAllResponse(apiPayload) {
  const root = apiPayload?.data !== undefined ? apiPayload.data : apiPayload;
  const rows = Array.isArray(root?.data) ? root.data : Array.isArray(root) ? root : [];
  if (!Array.isArray(rows)) return [];

  return rows
    .filter((row) => row != null && typeof row === "object")
    .map((row) => {
      const id = row.id ?? row.workItemId;
      const title = String(row.name ?? row.title ?? row.summary ?? "").trim();
      const issueKey = String(
        row.itemKey ?? row.issueKey ?? row.workItemKey ?? row.key ?? ""
      ).trim();
      const type =
        typeof row.type === "object" && row.type != null
          ? String(row.type.name ?? "").trim()
          : String(row.type ?? row.typeName ?? "").trim();
      const rawPid = row.projectId ?? row.project_id ?? row.project?.id;
      const projectId =
        rawPid != null && String(rawPid).trim() !== "" ? String(rawPid).trim() : undefined;
      const projectName = String(
        row.projectName ?? row.project?.name ?? row.project_name ?? ""
      ).trim();
      const clientName = String(
        row.clientName ??
          (typeof row.client === "string" ? row.client : null) ??
          row.client?.name ??
          row.client?.clientName ??
          ""
      ).trim();
      return {
        id,
        title,
        issueKey,
        type: type || undefined,
        projectId,
        projectName: projectName || undefined,
        clientName: clientName || undefined,
      };
    })
    .filter((row) => row.id != null && String(row.id).trim() !== "");
}

/**
 * GET /work-items/all — Log Time modal picker; merges flat `{ projectName, name }` rows with
 * normalized list fields (type, parent, itemKey) when both shapes appear.
 */
export function mapLogPickerWorkItemsResponse(apiPayload) {
  const flatList = mapMyWorkItemsAllResponse(apiPayload);
  const { list: normalizedList } = mapWorkItemsListResponse(apiPayload);
  const fullList = Array.isArray(normalizedList) ? normalizedList : [];

  if (!flatList.length) return fullList;
  if (!fullList.length) {
    return flatList.map((row) => ({
      ...row,
      itemKey: row.issueKey ?? row.itemKey,
      projectName: resolveWorkItemProjectName(row),
    }));
  }

  const fullById = new Map(fullList.map((row) => [String(row.id), row]));
  return flatList.map((row) => {
    const full = fullById.get(String(row.id));
    const merged = { ...(full ?? {}), ...row };
    return {
      ...merged,
      itemKey: row.issueKey ?? full?.issueKey ?? full?.itemKey ?? merged.itemKey,
      issueKey: row.issueKey ?? full?.issueKey ?? merged.issueKey,
      title: row.title ?? full?.title ?? merged.title,
      projectName: resolveWorkItemProjectName(row) || resolveWorkItemProjectName(full),
      clientName: row.clientName ?? full?.clientName ?? merged.clientName,
      type: row.type ?? full?.type ?? merged.type,
      parentTitle: full?.parentTitle ?? merged.parentTitle,
      parent: full?.parent ?? merged.parent,
      projectId: row.projectId ?? full?.projectId ?? merged.projectId,
    };
  });
}

/** Item key for pickers (API `itemKey` or legacy `issueKey`). */
export function resolveWorkItemItemKey(item) {
  if (!item || typeof item !== "object") return "";
  return String(item.itemKey ?? item.issueKey ?? item.workItemKey ?? item.key ?? "").trim();
}

/** Work type label (Epic, Task, Bug, …). */
export function resolveWorkItemTypeLabel(item) {
  if (!item || typeof item !== "object") return "";
  if (typeof item.type === "object" && item.type != null) {
    return String(item.type.name ?? "").trim();
  }
  return String(item.type ?? item.typeName ?? "").trim();
}

/** Client display name on work-item rows (`clientName` from API). */
export function resolveWorkItemClientName(item) {
  if (!item || typeof item !== "object") return "";
  return String(
    item.clientName ??
      (typeof item.client === "string" ? item.client : null) ??
      item.client?.name ??
      item.client?.clientName ??
      ""
  ).trim();
}

/** Project display name on work-item rows (`projectName` from GET /work-items/all). */
export function resolveWorkItemProjectName(item) {
  if (!item || typeof item !== "object") return "";
  if (typeof item.project === "object" && item.project != null) {
    return String(
      item.projectName ?? item.project_name ?? item.project.name ?? item.project.projectName ?? ""
    ).trim();
  }
  if (typeof item.project === "string") {
    return String(item.projectName ?? item.project_name ?? item.project ?? "").trim();
  }
  return String(item.projectName ?? item.project_name ?? "").trim();
}

/** Parent epic segment for task/bug rows (key · title); empty for epics and orphans. */
export function resolveWorkItemParentEpicSegment(item) {
  if (!item || typeof item !== "object") return "";
  const typeLabel = resolveWorkItemTypeLabel(item).toLowerCase();
  if (typeLabel.includes("epic")) return "";

  const epicName = String(
    item.epicName ?? item.epic_name ?? item.parentEpicName ?? item.parentEpicTitle ?? ""
  ).trim();
  if (epicName) return epicName;

  const parentTitle = String(item.parentTitle ?? "").trim();
  const parent = item.parent;
  let parentKey = "";
  let parentName = parentTitle;
  if (typeof parent === "object" && parent != null) {
    parentKey = String(parent.itemKey ?? parent.issueKey ?? parent.key ?? "").trim();
    if (!parentName) {
      parentName = String(parent.title ?? parent.summary ?? parent.name ?? "").trim();
    }
  }
  return [parentKey, parentName].filter(Boolean).join(" · ");
}

/** Current work item key · title for picker rows. */
export function resolveWorkItemWorkSegment(item) {
  if (!item || typeof item !== "object") return "";
  const key = resolveWorkItemItemKey(item);
  const title = String(item.title ?? item.name ?? item.summary ?? "").trim();
  return [key, title].filter(Boolean).join(" · ") || title || key || "";
}

/** Client · project · parent epic prefix for Log Time dropdown sublabel. */
export function buildLogTimePickerPathPrefix(item) {
  if (!item || typeof item !== "object") return "";
  return [
    resolveWorkItemClientName(item),
    resolveWorkItemProjectName(item),
    resolveWorkItemParentEpicSegment(item),
  ]
    .filter(Boolean)
    .join(" · ");
}

/**
 * Log Time issue dropdown label: Client · Project · Epic · task/bug (type via icon only).
 */
export function workItemToLogTimePickerLabel(item) {
  if (!item || typeof item !== "object") return "";
  const prefix = buildLogTimePickerPathPrefix(item);
  const workSegment = resolveWorkItemWorkSegment(item);
  const full = [prefix, workSegment].filter(Boolean).join(" · ");
  if (full) return full;
  return workItemToTimesheetTaskLabel(item);
}

/** Dropdown option for Log Time work-item picker (value, label, type icon). */
export function workItemToLogTimePickerOption(item) {
  const typeLabel = resolveWorkItemTypeLabel(item);
  const { Icon, colorClass } = getWorkItemTypeIconConfig(typeLabel);
  const workLabel = resolveWorkItemWorkSegment(item) || String(item?.id ?? "");
  const pathSublabel = buildLogTimePickerPathPrefix(item);
  const fullLabel = workItemToLogTimePickerLabel(item) || workLabel;

  return {
    value: item?.id,
    label: workLabel,
    sublabel: pathSublabel || undefined,
    displayLabel: fullLabel,
    searchText: fullLabel.replace(/ · /g, " "),
    icon: Icon,
    color: colorClass,
  };
}

/** Timesheet / filter list label: `clientName · project · key · title` (omits missing segments). */
export const workItemToTimesheetTaskLabel = (item) => {
  if (!item || typeof item !== "object") return "";
  const clientName = resolveWorkItemClientName(item);
  const title = String(item.title ?? item.name ?? item.summary ?? "").trim();
  const projectName = String(item.projectName ?? item.project ?? "").trim();
  const key = String(item.issueKey ?? item.workItemKey ?? item.itemKey ?? "").trim();
  const workPart = [key, title].filter(Boolean).join(" · ");
  const prefix = [clientName, projectName].filter(Boolean).join(" · ");
  if (prefix && workPart) return `${prefix} · ${workPart}`;
  if (prefix) return prefix;
  if (workPart) return workPart;
  return title || key || "";
};

/** Breadcrumb for a normalized timesheet row: Client › Project › Task. */
export function formatTimesheetEntryPath(entry) {
  if (!entry || typeof entry !== "object") return "—";
  const client =
    String(entry.clientName ?? "").trim() ||
    (entry.client && entry.client !== "—" ? String(entry.client).trim() : "");
  const project = String(entry.project ?? entry.projectName ?? "").trim();
  const task = String(entry.task ?? "").trim();
  const parts = [client, project, task].filter(Boolean);
  return parts.length ? parts.join(" › ") : "—";
}

const formatLogDateDisplay = (logDate) => {
  if (!logDate) return "Unknown date";
  const s = formatDate(logDate);
  return s === "N/A" ? String(logDate) : s;
};

/** Resolve time log id from POST/PATCH `{ success, data: { id } }` (or flat) responses. */
export function resolveTimeLogIdFromApiResponse(response) {
  const root = response?.data ?? response;
  const row = root?.data ?? root;
  if (row == null || typeof row !== "object") return null;
  const id = row.id ?? row.timeLogId;
  return id != null && String(id).trim() !== "" ? String(id) : null;
}

/**
 * PATCH /time-logs/my/:id — send only changed fields (matches backend partial update contract).
 * Non-admin: never sends billableMinutes. Admin may send billable-only, logged+comment, etc.
 */
export function buildMyTimeLogPatchBody({
  isAdmin = false,
  minutesLogged,
  logDate,
  comment,
  billableMinutes,
  initialMinutesLogged = null,
  initialBillableMinutes = null,
  initialLogDate = null,
  initialComment = "",
}) {
  const body = {};
  const initialYmd = initialLogDate != null ? String(initialLogDate).slice(0, 10) : null;
  const logged =
    minutesLogged != null && Number.isFinite(Number(minutesLogged)) ? Number(minutesLogged) : null;
  const loggedChanged =
    logged != null && (initialMinutesLogged == null || logged !== Number(initialMinutesLogged));
  const logDateChanged = Boolean(logDate && initialYmd && logDate !== initialYmd);
  const commentChanged = String(initialComment ?? "").trim() !== String(comment ?? "").trim();

  const initialBillable =
    initialBillableMinutes != null && Number.isFinite(Number(initialBillableMinutes))
      ? Number(initialBillableMinutes)
      : initialMinutesLogged != null && Number.isFinite(Number(initialMinutesLogged))
        ? Number(initialMinutesLogged)
        : null;
  const billable =
    billableMinutes != null && Number.isFinite(Number(billableMinutes))
      ? Number(billableMinutes)
      : null;
  const billableChanged =
    isAdmin && billable != null && initialBillable != null && billable !== initialBillable;

  if (loggedChanged && logged != null) {
    body.minutesLogged = logged;
    if (logDate) body.logDate = logDate;
  } else if (logDateChanged && logDate) {
    body.logDate = logDate;
  }

  if (commentChanged) {
    body.comment = comment == null ? null : String(comment).trim() || null;
  }

  if (isAdmin && billableChanged) {
    body.billableMinutes = billable;
  } else if (isAdmin && loggedChanged && billable != null) {
    body.billableMinutes = billable;
  }

  return body;
}

/**
 * Normalize GET /time-logs/my or GET /time-logs → { list } for timesheet UI.
 * API shape: { success, data: { data: [rows], meta } }
 * Date column uses `logDate` (not `created_at`); falls back to `created_at` only when logDate is missing.
 */
export const normalizeMyTimeLogsResponse = (responseData) => {
  const raw = responseData?.data ?? responseData;
  const payload = raw?.data ?? raw;
  const rows = Array.isArray(payload) ? payload : (payload?.data ?? []);
  if (!Array.isArray(rows)) return { list: [], meta: null };

  const list = rows.map((row) => {
    const logDate = row.logDate ?? row.log_date ?? "";
    const loggedAt = row.created_at ?? row.createdAt ?? null;
    const dayLabel =
      logDate != null && String(logDate).trim() !== ""
        ? formatLogDateDisplay(logDate)
        : loggedAt != null && String(loggedAt).trim() !== ""
          ? (() => {
              const s = formatDate(loggedAt);
              return s === "N/A" ? String(loggedAt) : s;
            })()
          : "Unknown date";
    const title = row.workItemTitle ?? "";
    const key = row.workItemKey ?? "";
    const taskLabel = [key, title].filter(Boolean).join(" · ") || title || key || "—";
    const clientNameRaw =
      row.clientName ??
      (typeof row.client === "string" ? row.client : null) ??
      row.client?.name ??
      row.client?.clientName ??
      "";
    const clientLabel = String(clientNameRaw).trim() || "—";
    const clientName = clientLabel !== "—" ? clientLabel : "";
    const minutesLogged = Number(row.minutesLogged) || 0;
    const billableRaw = row.billableMinutes ?? row.minutesBillable ?? row.billable_minutes;
    const billableMinutes =
      billableRaw != null && billableRaw !== "" && Number.isFinite(Number(billableRaw))
        ? Number(billableRaw)
        : minutesLogged;
    const teamMemberRaw =
      row.userName ??
      row.loggedBy?.name ??
      row.loggedByName ??
      row.memberName ??
      (typeof row.member === "string" ? row.member : null) ??
      row.assignee ??
      row.owner ??
      "";
    const teamMember =
      String(teamMemberRaw).trim() || (typeof row.loggedBy === "string" ? row.loggedBy.trim() : "");

    return {
      id: row.id,
      workItemId: row.workItemId,
      workItemKey: row.workItemKey ?? "",
      workItemTitle: row.workItemTitle ?? "",
      projectId: row.projectId,
      minutesLogged,
      billableMinutes,
      logDate: logDate || null,
      createdAt: loggedAt,
      day: dayLabel,
      dateLabel: dayLabel,
      clientName,
      client: clientLabel,
      project: String(row.projectName ?? row.project ?? "").trim() || "—",
      task: taskLabel,
      teamMember: teamMember || "—",
      duration: formatMinutesToDuration(minutesLogged),
      billableDuration:
        billableMinutes < minutesLogged ? formatMinutesToDuration(billableMinutes) : null,
      time: formatTime(loggedAt ?? row.logDate),
      description: row.comment ?? "",
      isBillable: row.isBillable != null ? Boolean(row.isBillable) : billableMinutes > 0,
      billable: row.isBillable != null ? Boolean(row.isBillable) : billableMinutes > 0,
    };
  });

  const meta = payload?.meta ?? raw?.meta ?? null;
  return { list, meta };
};
